/**
 * Outer ingest seam — orchestrate a parsed-source upload end to end.
 *
 * Steps: parse → dedup → resolve athlete → archive (Storage + athlete_files
 * via archiveArtifact) → insert upload row → insert data rows → cleanup
 * chain on failure.
 *
 * The status enum and cleanup invariants belong to this orchestrator;
 * descriptors don't see them. Each parsed source provides a descriptor
 * (see src/features/<source>/source.js) describing its parser, the
 * tables to write to, and how to build the rows.
 *
 * Returns one of:
 *   { status: 'success', uploadId, fileId, athleteId, athleteName, storagePath, archived, ...extras }
 *   { status: 'duplicate', upload }
 *   { status: 'no-athlete', pitcher: {first, last}, ...extras }
 *   { status: 'invalid-filename', filename }
 *   { status: 'empty', filteredCount, unknownColumns }
 *
 * Throws only on unexpected DB/Storage errors. The named statuses are
 * "expected" branches the UI handles directly.
 */

import { supabase } from './supabase.js';
import { archiveArtifact, removeArchivedArtifact } from './archive.js';

export async function ingestParsedSource({
  csvText,
  csvFile,
  filename,
  descriptor,
  sourceParams = {},
}) {
  // 1. Parse via the descriptor's parse wrapper (it adds athleteName + rows
  //    on top of the source-native parser output).
  const parseResult = descriptor.parse(csvText);
  const rows = parseResult.rows;
  if (!rows || rows.length === 0) {
    return {
      status: 'empty',
      filteredCount: parseResult.filteredCount ?? 0,
      unknownColumns: parseResult.unknownColumns ?? [],
    };
  }

  // 2. Extract dedup key. Descriptors take {filename, parseResult} so they
  //    can fall back to content-derived keys when the filename has been
  //    renamed by a coach (e.g. "2026-05-10_bullpen_pt_2.csv" instead of
  //    Rapsodo's auto-generated "PracticeSession_<24hex>_Data_*.csv").
  const dedupKey = descriptor.extractDedupKey({ filename, parseResult });
  if (!dedupKey) {
    return { status: 'invalid-filename', filename };
  }

  // 3. Dedup check.
  const { data: existing, error: dedupError } = await supabase
    .from(descriptor.uploadTable)
    .select('id, uploaded_at, row_count, athlete_id')
    .eq(descriptor.dedupColumn, dedupKey)
    .maybeSingle();
  if (dedupError) throw dedupError;
  if (existing) {
    return { status: 'duplicate', upload: existing };
  }

  // 4. Resolve athlete from parse result.
  const athleteName = parseResult.athleteName;
  if (!athleteName) {
    // No identifiable athlete (e.g. PR file mixes athletes).
    return {
      status: 'no-athlete',
      pitcher: { first: 'Multiple', last: 'athletes in one file' },
      ...descriptor.buildEmptyExtras({ parseResult }),
    };
  }
  const athlete = await findAthleteByName(athleteName.first, athleteName.last);
  if (!athlete) {
    return {
      status: 'no-athlete',
      pitcher: athleteName,
      ...descriptor.buildEmptyExtras({ parseResult }),
    };
  }

  // 5. Build session metadata + archive the file (Storage + athlete_files).
  const lastRowTime = rows[rows.length - 1].thrown_at;
  const sessionDate = utcToChicagoDate(lastRowTime);
  const blob = csvFile ?? new Blob([csvText], { type: 'text/csv' });
  const archive = await archiveArtifact({
    blob,
    filename,
    athleteId: athlete.id,
    sessionDate,
    source: descriptor.archiveSource,
    uploadedAt: lastRowTime,
  });
  const storagePath = archive.archived ? archive.storagePath : null;

  // 6. Insert the upload row. file_path is whatever Storage gave us.
  const uploadRow = descriptor.buildUploadRow({
    athlete,
    dedupKey,
    parseResult,
    filename,
    storagePath,
    sessionDate,
    sourceParams,
  });
  const { data: upload, error: uploadError } = await supabase
    .from(descriptor.uploadTable)
    .insert(uploadRow)
    .select()
    .single();
  if (uploadError) {
    if (archive.archived) await removeArchivedArtifact(archive.storagePath, archive.fileId);
    throw uploadError;
  }

  // 7. Insert the data rows.
  const dataRows = descriptor.buildDataRows({
    uploadId: upload.id,
    athlete,
    parseResult,
    sourceParams,
  });
  const { error: dataError } = await supabase.from(descriptor.dataTable).insert(dataRows);
  if (dataError) {
    if (archive.archived) await removeArchivedArtifact(archive.storagePath, archive.fileId);
    await supabase.from(descriptor.uploadTable).delete().eq('id', upload.id);
    throw dataError;
  }

  return {
    status: 'success',
    uploadId: upload.id,
    fileId: archive.archived ? archive.fileId : null,
    athleteId: athlete.id,
    athleteName: `${athlete.first_name} ${athlete.last_name}`,
    storagePath,
    archived: archive.archived,
    ...descriptor.buildSuccessExtras({ parseResult, sourceParams }),
  };
}

/**
 * Look up an athlete by exact First+Last match. Used by ingestParsedSource
 * and exposed for any caller that needs the same lookup behavior.
 */
export async function findAthleteByName(firstName, lastName) {
  const { data, error } = await supabase
    .from('athletes')
    .select('id, first_name, last_name')
    .eq('first_name', firstName)
    .eq('last_name', lastName)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function utcToChicagoDate(isoUtc) {
  // 'en-CA' formats as YYYY-MM-DD which Postgres date columns accept directly.
  return new Date(isoUtc).toLocaleDateString('en-CA', {
    timeZone: 'America/Chicago',
  });
}
